import {t, Trans} from '@lingui/macro';
import React from 'react';

import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {
  VirtualTableColumnCellsEnum,
  virtualTableRowCheckboxWrapperWidth,
} from '../../../components/common/table/helpers/tableHelpers';
import {TableColumn} from '../../../components/common/table/tableColumn';
import {
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
  CustomFieldsEnum,
} from '../../../models/api/customFieldModel';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';

/*
 * Constants.
 */

export const customFieldsTableNameColumnId = 'custom-fields-table-name';
export const customFieldsTableTypeColumnId = 'custom-fields-table-type';
export const customFieldsTableCreateDateColumnId = 'custom-fields-table-date';

const cardDefaultFieldId = {
  NAME: -1,
  EMAIL: -2,
  PHONE_NUMBER: -3,
  TWITTER: -4,
  TWITTER_DM: -5,
  CUSTOM_CHANNEL: -6,
  DESCRIPTION: -7,
  LINK: -8,
};

export enum CustomFieldsTableColumnsEnum {
  CREATED_DATE = 'created_date',
  NAME = 'name',
  TYPE = 'type',
}

export const customFieldsTableColumns: ReadonlyArray<TableColumn<CustomFieldsTableColumnsEnum>> = [
  {
    id: customFieldsTableNameColumnId,
    title: <Trans id="custom-fields-table-column-name">Name</Trans>,
    type: CustomFieldsTableColumnsEnum.NAME,
    cellType: VirtualTableColumnCellsEnum.CUSTOM_FIELD,
    settings: {
      sticky: {
        isSticky: true,
        stickyOffset: virtualTableRowCheckboxWrapperWidth,
      },
      cellWidth: {
        shouldFillRemainingWidth: true,
        minWidth: 375,
        maxWidth: 375,
      },
      loading: {
        avatarSize: VisualSizesEnum.LARGE,
      },
    },
  },
  {
    id: customFieldsTableTypeColumnId,
    title: <Trans id="custom-fields-table-column-type">Type</Trans>,
    type: CustomFieldsTableColumnsEnum.TYPE,
    cellType: VirtualTableColumnCellsEnum.TEXT,
    settings: {
      cellWidth: {
        minWidth: 152,
        maxWidth: 152,
      },
    },
  },
  {
    id: customFieldsTableCreateDateColumnId,
    title: <Trans id="custom-fields-table-column-created-date">Date Created</Trans>,
    type: CustomFieldsTableColumnsEnum.CREATED_DATE,
    cellType: VirtualTableColumnCellsEnum.DATE,
    settings: {
      cellWidth: {
        minWidth: 152,
        maxWidth: 152,
      },
    },
  },
];

/*
 * Helpers.
 */

export function buildDefaultFieldsForResourceType(
  resourceType: CustomFieldResourceTypesEnum,
  createdAt: number,
  localize: Localizer,
): ReadonlyArray<CustomFieldModel> {
  switch (resourceType) {
    case CustomFieldResourceTypesEnum.CARD:
      return [
        buildDefaultCustomField({
          id: cardDefaultFieldId.NAME,
          name: localize(t('contact-default-field-name-title')`Name`),
          description: localize(t('contact-default-field-name-description')`The contact’s name`),
          type: CustomFieldsEnum.STRING,
          resourceType: CustomFieldResourceTypesEnum.CARD,
          createdAt,
        }),
        buildDefaultCustomField({
          id: cardDefaultFieldId.EMAIL,
          name: localize(t('contact-default-field-email-title')`Email`),
          description: localize(t('contact-default-field-email-description')`The contact’s email addresses`),
          type: CustomFieldsEnum.STRING,
          resourceType: CustomFieldResourceTypesEnum.CARD,
          createdAt,
        }),
        buildDefaultCustomField({
          id: cardDefaultFieldId.PHONE_NUMBER,
          name: localize(t('contact-default-field-phone-number-title')`Phone number`),
          description: localize(
            t('contact-default-field-phone-number-description')`The contact’s phone numbers`,
          ),
          type: CustomFieldsEnum.STRING,
          resourceType: CustomFieldResourceTypesEnum.CARD,
          createdAt,
        }),
        buildDefaultCustomField({
          id: cardDefaultFieldId.TWITTER,
          name: localize(t('contact-default-field-twitter-title')`Twitter`),
          description: localize(t('contact-default-field-twitter-description')`The contact’s Twitter handle`),
          type: CustomFieldsEnum.STRING,
          resourceType: CustomFieldResourceTypesEnum.CARD,
          createdAt,
        }),
        buildDefaultCustomField({
          id: cardDefaultFieldId.TWITTER_DM,
          name: localize(t('contact-default-field-twitter-dm-title')`Twitter DM`),
          description: localize(
            t(
              'contact-default-field-twitter-dm-description',
            )`The contact’s twitter handle for direct messages`,
          ),
          type: CustomFieldsEnum.STRING,
          resourceType: CustomFieldResourceTypesEnum.CARD,
          createdAt,
        }),
        buildDefaultCustomField({
          id: cardDefaultFieldId.CUSTOM_CHANNEL,
          name: localize(t('contact-default-field-custom-channel-title')`Custom channel`),
          description: localize(
            t('contact-default-field-custom-channel-description')`The contact’s custom channel handle`,
          ),
          type: CustomFieldsEnum.STRING,
          resourceType: CustomFieldResourceTypesEnum.CARD,
          createdAt,
        }),
        buildDefaultCustomField({
          id: cardDefaultFieldId.DESCRIPTION,
          name: localize(t('contact-default-field-description-title')`Description`),
          description: localize(
            t('contact-default-field-description-description')`A short blurb about the contact`,
          ),
          type: CustomFieldsEnum.STRING,
          resourceType: CustomFieldResourceTypesEnum.CARD,
          createdAt,
        }),
        buildDefaultCustomField({
          id: cardDefaultFieldId.LINK,
          name: localize(t('contact-default-field-link-title')`Link`),
          description: localize(t('contact-default-field-link-description')`A web address`),
          type: CustomFieldsEnum.STRING,
          resourceType: CustomFieldResourceTypesEnum.CARD,
          createdAt,
        }),
      ];
    case CustomFieldResourceTypesEnum.ACCOUNT:
      return [
        buildDefaultCustomField({
          id: -1,
          name: localize(t('account-default-field-name-title')`Name`),
          description: localize(
            t('account-default-field-name-description')`The name of the company or organization`,
          ),
          type: CustomFieldsEnum.STRING,
          resourceType: CustomFieldResourceTypesEnum.ACCOUNT,
          createdAt,
        }),
        buildDefaultCustomField({
          id: -2,
          name: localize(t('account-default-field-email-domain-title')`Email domain`),
          description: localize(
            t(
              'account-default-field-email-domain-description',
            )`The email domain(s) of the company or organization`,
          ),
          type: CustomFieldsEnum.EMAIL_DOMAIN,
          resourceType: CustomFieldResourceTypesEnum.ACCOUNT,
          createdAt,
        }),
        buildDefaultCustomField({
          id: -3,
          name: localize(t('account-default-field-description-title')`Description`),
          description: localize(
            t(
              'account-default-field-description-description',
            )`A short blurb about the company or organization`,
          ),
          type: CustomFieldsEnum.STRING,
          resourceType: CustomFieldResourceTypesEnum.ACCOUNT,
          createdAt,
        }),
      ];
    default:
      return [];
  }
}

function buildDefaultCustomField({
  id,
  name,
  description,
  type,
  resourceType,
  createdAt,
}: Partial<CustomFieldModel>): CustomFieldModel {
  return {
    id: id || -1,
    name: name || '',
    description: description || '',
    type: type || CustomFieldsEnum.UNKNOWN,
    resourceType: resourceType! || CustomFieldResourceTypesEnum.CARD,
    createdAt: createdAt || Date.now(),
    // These settings do not matter, since we are just displaying
    updatedAt: createdAt || Date.now(),
    url: '',
    nameSlug: '',
    origin: '',
  };
}

export function isCustomFieldDefaultField(customField: CustomFieldModel) {
  return customField.id < 0;
}

export function computeDefaultCustomFieldIconName(customField: CustomFieldModel): IconName | undefined {
  if (customField.resourceType !== CustomFieldResourceTypesEnum.CARD) {
    return undefined;
  }
  switch (customField.id) {
    case cardDefaultFieldId.EMAIL:
      return 'channelEmail';
    case cardDefaultFieldId.PHONE_NUMBER:
      return 'channelCall';
    case cardDefaultFieldId.TWITTER:
    case cardDefaultFieldId.TWITTER_DM:
      return 'twitterFilled';
    case cardDefaultFieldId.CUSTOM_CHANNEL:
      return 'channelsFilled';
    case cardDefaultFieldId.LINK:
      return 'link';
    case cardDefaultFieldId.NAME:
    case cardDefaultFieldId.DESCRIPTION:
    default:
      return undefined;
  }
}
